/**
 * CaptureStep.tsx — Step 0: height/weight/email capture (lead-first).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the lead is captured BEFORE the first question — an abandoned journey is
 * still a lead. The metric↔imperial toggle converts values both ways (ft-in and
 * st-lb via journey/units.ts) and the canonical POST body is always cm/kg with
 * `displayUnits` recording what the patient typed. A failed lead POST never
 * blocks the patient (the funnel simply misses a row) — but the network error
 * is surfaced so a retry is possible (TA-06). `variant="meta"` renders the lean
 * paid-social version (metric-only, minimal chrome). Copy is pre-eligibility
 * PUBLIC surface: category-level only (R28). The single conversion CTA is
 * terracotta.
 *
 * SHORTER SINCE 2026-09-12 (CTO). The "what brings you here today" dropdown is
 * gone from this form and is now its own screen, `QualificationStep`, between
 * the projection interstitial and the questionnaire. This is the highest-
 * drop-off screen in the journey, and it was carrying a question that earns the
 * business a marketing segment and earns the patient nothing — so it is the
 * last place a non-essential field should live. Nothing about the compliance
 * boundary changed with it: the vocabulary is still commercial-only, and
 * `leadReadiness.ts` still holds the argument for why.
 *
 * DIRTY (navigation resilience): dirty while ANY field (either unit's
 * height/weight, or email) is non-empty and `onDone` has not yet fired —
 * once it fires the stage moves on and this component unmounts, so there is
 * nothing left to guard. No in-app Back button lives on this, the FIRST,
 * step — `useUnsavedChangesGuard` here only ever covers a real router
 * navigation (browser Back/tab close/refresh) via `useBlocker`/`beforeunload`.
 * It stays armed while the capture loader runs: the lead is still being saved.
 * The consent tick deliberately does NOT arm the guard: it is one click to
 * re-give and nothing has been sent, so a leave prompt over it would be
 * friction defending nothing.
 *
 * MARKETING CONSENT TICK (2026-09-23, clinic #218 — FLAG-GATED, DEFAULT OFF).
 * Behind `step0MarketingConsentEnabled()` (`VITE_STEP0_CONSENT_ENABLED=true`;
 * OFF everywhere until the CTO signs the DRAFT wording
 * `mc-2026-09-23-step0-d1`) the form ends with an UNTICKED email-consent box —
 * never pre-ticked (PECR reg. 22). GRANT-ONLY BY SHAPE: a tick puts
 * `marketingConsent: { email: true, wordingVersion }` on the lead POST; an
 * untick sends NOTHING — the key is absent, never `email: false`, which is the
 * server's 400 (see `LeadMarketingConsentGrant` in api/types). Wording is
 * `marketing/step0ConsentWording.ts`, rendered verbatim; while the flag is off
 * this screen is byte-identical to the pre-flag build (step0Consent.spec.tsx).
 *
 * THE CAPTURE LOADER (2026-09-25, CTO — "that button hangs there"). Once the
 * form passes its own checks, the form is REPLACED IN PLACE (same column, same
 * stage, no route change) by `CaptureLoader` — "Setting up your health check",
 * the checking loader's narrated checklist with Step 0's own steps — and
 * `onDone` fires when that checklist finishes, which is what brings the chart.
 * The checklist's first step ticks on the lead POST's own answer; the words,
 * the floor (~3.6s) and the still-working lines are `runCaptureBeat` in
 * `journeyLoaders.ts`. What did NOT change, deliberately:
 *   - a failed createLead still never blocks: the journey carries on without a
 *     `leadId` (the checklist says "Not saved this time" rather than ticking);
 *   - funnel C1 (`emitGenerateLeadOnce`) still fires at the instant createLead
 *     returns, inside the try below — never after the loader's floor;
 *   - a validation error still appears on the form at once, with no loader.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { createLead } from '../api/journey';
import { useAuth } from '../auth/AuthContext';
import { step0MarketingConsentEnabled } from '../config';
import { STEP0_CONSENT_WORDING } from '../marketing/step0ConsentWording';
import { emitGenerateLeadOnce } from '../tracking/generateLead';
import type { Attribution } from '../api/types';

/** Split a stored "First Last" display name into its parts (for prefilling). */
function splitName(fullName: string | null | undefined): { first: string; last: string } {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? '', last: parts.length > 1 ? parts.slice(1).join(' ') : '' };
}
import { CheckboxRow, Eyebrow, Field, Help, TextInput, Title } from '../ui/kit';
import { isUkMobile, ukMobileHint } from '../lib/ukMobile';
import { LeaveConfirmDialog } from '../navigation/LeaveConfirmDialog';
import { useUnsavedChangesGuard } from '../navigation/useUnsavedChangesGuard';
import { usePrefersReducedMotion } from '../ui/motion';
import { CaptureLoader } from './CaptureLoader';
import { runCaptureBeat, type CaptureBeatView } from './journeyLoaders';
import { JourneyButton } from './JourneyUi';
import { JourneyScreen } from './JourneyScreen';
import { NO_CHARGE_UNTIL_APPROVED, TWO_MINUTE_CHECK } from './serviceClaims';
import { cmToFtIn, ftInToCm, kgToStLb, stLbToKg } from './units';

/** What Step 0 hands to the journey once the lead POST has been fired. */
export interface CaptureResult {
  heightCm: number;
  weightKg: number;
  email: string;
  leadId?: string;
  /**
   * The first name exactly as typed here (trimmed) — held IN MEMORY by the
   * journey only to greet the patient on the Result screen (premium pass,
   * 2026-09-24). It is the value this form already POSTs with the lead; it is
   * never logged, stored or sent anywhere else from here.
   */
  firstName?: string;
}

function num(value: string): number {
  return value.trim() === '' ? NaN : Number(value);
}

/** Step-0 capture with the working metric↔imperial toggle. */
export function CaptureStep({
  clinicSlug,
  clinicName,
  attribution,
  variant,
  emailLabel = 'Email address',
  onDone,
  onLoaderShown,
}: {
  clinicSlug: string;
  clinicName: string;
  attribution?: Attribution;
  variant: 'default' | 'meta';
  /**
   * The email field's label. Defaults to the patient-facing copy; the admin
   * fill-on-behalf flow (AdminFormPage) passes "Patient's email address" so
   * staff enter the PATIENT'S details, never their own — the patient-facing
   * journey is untouched by defaulting.
   */
  emailLabel?: string;
  onDone: (result: CaptureResult) => void;
  /**
   * Told `true` while the capture loader is on screen and `false` when it goes
   * (JourneyPage holds the call-back offer back meanwhile: the loader stands
   * alone, like the checking loader). Optional; must be a stable function.
   */
  onLoaderShown?: (shown: boolean) => void;
}) {
  const [unit, setUnit] = useState<'metric' | 'imperial'>('metric');
  const [cm, setCm] = useState('');
  const [kg, setKg] = useState('');
  const [feet, setFeet] = useState('');
  const [inches, setInches] = useState('');
  const [stones, setStones] = useState('');
  const [pounds, setPounds] = useState('');
  // Prefill from the session ONLY for a signed-in PATIENT (the journey's sole
  // authed caller — staff fill-on-behalf uses SinglePageForm, so this never
  // leaks a clinician's own details). Height/weight are never stored, so they
  // always start blank. A guest gets empty fields as before.
  const { me } = useAuth();
  const authedPatient = me?.role === 'PATIENT' ? me : null;
  const prefill = splitName(authedPatient?.fullName);
  const [firstName, setFirstName] = useState(prefill.first);
  const [lastName, setLastName] = useState(prefill.last);
  const [email, setEmail] = useState(authedPatient?.email ?? '');
  const [phone, setPhone] = useState('');
  // The Step-0 consent tick — ALWAYS unticked to start (PECR reg. 22: never
  // pre-ticked, and no caller may seed it). Read even while the flag is off so
  // the hook order never changes with configuration; the flag gates the render
  // and the wire, not the hook.
  const [marketingEmail, setMarketingEmail] = useState(false);
  const consentOffered = step0MarketingConsentEnabled();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // THE ERROR IS BROUGHT INTO VIEW (2026-09-24). On a phone the CTA now sticks
  // to the bottom of the screen (JourneyUi `ActionRow`, CTO 2026-09-24), so it
  // can be pressed from half-way up this long form — and the validation line
  // renders down by the button's SLOT, off-screen. `role="alert"` still speaks
  // it, but a sighted patient would see nothing happen. Each failed submit
  // bumps the counter (a repeat of the same message must scroll again) and the
  // effect scrolls the line to the nearest edge; tokens.css's scroll padding
  // keeps it above the sticky bar. 'nearest' is a no-op when it is already on
  // screen, which is every desktop case (the row is inline there).
  const [errorSeq, setErrorSeq] = useState(0);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [done, setDone] = useState(false);
  // What the capture loader draws; null while the form is on screen.
  const [loaderView, setLoaderView] = useState<CaptureBeatView | null>(null);
  const loaderShown = loaderView !== null;
  const dirty =
    !done &&
    Boolean(cm.trim() || kg.trim() || feet.trim() || inches.trim() || stones.trim() || pounds.trim() || email.trim() || phone.trim());
  // Default arming: a direct land on `/weightloss` has no prior remix-router
  // entry, so browser Back would otherwise skip useBlocker (delta == null).
  // Deferred arm + collapse on clean (see useUnsavedChangesGuard) keeps the
  // history stack free of orphan same-URL slots after submit.
  const guard = useUnsavedChangesGuard(dirty);

  useEffect(() => {
    if (!loaderShown || !onLoaderShown) return;
    onLoaderShown(true);
    return () => onLoaderShown(false);
  }, [loaderShown, onLoaderShown]);

  useEffect(() => {
    if (errorSeq === 0) return;
    // Optional call: jsdom (and very old engines) have no scrollIntoView.
    errorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [errorSeq, reducedMotion]);

  function fail(message: string) {
    setError(message);
    setErrorSeq((n) => n + 1);
  }

  function switchUnit(next: 'metric' | 'imperial') {
    if (next === unit) return;
    if (next === 'imperial') {
      const h = num(cm);
      if (Number.isFinite(h) && h > 0) {
        const fi = cmToFtIn(h);
        setFeet(String(fi.feet));
        setInches(String(fi.inches));
      }
      const w = num(kg);
      if (Number.isFinite(w) && w > 0) {
        const sl = kgToStLb(w);
        setStones(String(sl.stones));
        setPounds(String(sl.pounds));
      }
    } else {
      const f = num(feet);
      const i = num(inches);
      if (Number.isFinite(f) && (f > 0 || (Number.isFinite(i) && i > 0))) {
        setCm(String(ftInToCm(f || 0, Number.isFinite(i) ? i : 0)));
      }
      const s = num(stones);
      const l = num(pounds);
      if (Number.isFinite(s) && (s > 0 || (Number.isFinite(l) && l > 0))) {
        setKg(String(stLbToKg(s || 0, Number.isFinite(l) ? l : 0)));
      }
    }
    setUnit(next);
  }

  function readCanonical(): { heightCm: number; weightKg: number } | null {
    let heightCm: number;
    let weightKg: number;
    if (unit === 'imperial') {
      const f = num(feet);
      const i = num(inches);
      const s = num(stones);
      const l = num(pounds);
      heightCm = ftInToCm(Number.isFinite(f) ? f : 0, Number.isFinite(i) ? i : 0);
      weightKg = stLbToKg(Number.isFinite(s) ? s : 0, Number.isFinite(l) ? l : 0);
    } else {
      heightCm = num(cm);
      weightKg = num(kg);
    }
    if (!Number.isFinite(heightCm) || heightCm < 100 || heightCm > 272) return null;
    if (!Number.isFinite(weightKg) || weightKg < 30 || weightKg > 500) return null;
    return { heightCm, weightKg };
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const canonical = readCanonical();
    if (!canonical) {
      fail('Please check your height and weight — they help us work out whether treatment could suit you.');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      fail('Please add your first and last name.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      fail('Please add your email address so we can save your progress.');
      return;
    }
    // Phone is OPTIONAL on Step 0 (keeps the check light); if given it must be a
    // plausible number so the clinician has something real to call.
    // A UK MOBILE when given (2026-09-11) — still optional here, but a number
    // we cannot text is a follow-up that silently never lands. clinic-service
    // enforces the same rule and is the control; this says WHY at the field,
    // because this step does not parse a server 400 into a sentence.
    if (phone.trim().length > 0 && !isUkMobile(phone)) {
      fail(`${ukMobileHint(phone)} Or leave it blank.`);
      return;
    }
    setError(null);
    setBusy(true);
    // The loader replaces the form NOW, before the POST leaves; its first step
    // ticks on the answer below (`settleSave`), never on a timer alone.
    let settleSave: (saved: boolean) => void = () => undefined;
    const checklist = runCaptureBeat(
      new Promise<boolean>((resolve) => {
        settleSave = resolve;
      }),
      setLoaderView,
    );
    let leadId: string | undefined;
    try {
      const lead = await createLead({
        clinicSlug,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        // Only send a phone when there is one — an empty string would 400. Any
        // non-empty value is already a UK mobile by here (checked above).
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        heightCm: canonical.heightCm,
        weightKg: canonical.weightKg,
        displayUnits: unit,
        // NO `intent` HERE ANY MORE (2026-09-12). The readiness question left
        // this form for its own screen before the questionnaire, which is after
        // this POST has already run — `QualificationStep` records the answer
        // against this lead with `recordLeadReadiness()`. The key stays on
        // `LeadCaptureRequest` for the Webflow embed, which still posts it.
        ...(attribution ? { attribution } : {}),
        // GRANT-ONLY BY SHAPE (clinic #218): the key rides ONLY on a tick, as
        // the literal `email: true` plus the version of the words the tick was
        // shown. An unticked box contributes NOTHING here — no key, and never
        // `email: false`, which the server 400s (a lead create records at most
        // a grant; withdrawal has its own lanes). `consentOffered` keeps the
        // flag-off wire byte-identical even if state could somehow be true.
        ...(consentOffered && marketingEmail
          ? { marketingConsent: { email: true as const, wordingVersion: STEP0_CONSENT_WORDING.version } }
          : {}),
      });
      leadId = lead.leadId;
      // Funnel C1 — lead captured (PHI-safe: id only). Through the SHARED
      // once-only emitter, not `trackGenerateLead` directly: the widget hand-off
      // in JourneyPage pushes the same event for leads the portal did not
      // create, and one lead must produce exactly one C1 whichever door it came
      // through. A double-count reads as growth, so it is the failure that has
      // to be impossible rather than unlikely — see tracking/generateLead.ts.
      //
      // `minted-here` is this lane's answer to "how do you know it is new?":
      // `createLead` has just returned the id, so no earlier document can have
      // seen it. The widget lane cannot say that and asks the server instead.
      emitGenerateLeadOnce(leadId, 'minted-here');
    } catch {
      // Never block the patient on funnel plumbing — proceed without a leadId.
    } finally {
      // "Saved" only when a lead came back; otherwise the step says it was not.
      settleSave(leadId !== undefined);
    }
    // The chart arrives when the checklist has finished — never before the
    // POST has answered, and never before each step has been readable.
    await checklist;
    // COMMIT `done` BEFORE HANDING OVER. That commit is where the unsaved-
    // changes guard sees the form clean and drops the same-URL history entry it
    // pushed to catch browser Back. Batched with the stage change, this step
    // unmounted first and left that entry behind — so the first Back from the
    // chart did nothing. The checklist's timers make the arming certain on
    // every submit (callbackHistory.spec "Scenario A" caught it).
    flushSync(() => setDone(true));
    onDone({ ...canonical, email: email.trim(), leadId, firstName: firstName.trim() });
  }

  const toggleBtn = (active: boolean): React.CSSProperties => ({
    border: 0,
    background: active ? 'var(--gps-surface)' : 'transparent',
    color: active ? 'var(--gps-green-text)' : 'var(--gps-muted)',
    font: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    // ≥44px tap target on phones (was 40px — E3 #10).
    padding: '11px 18px',
    minHeight: '44px',
    borderRadius: 'var(--gps-r-pill)',
    boxShadow: active ? 'var(--gps-shadow)' : 'none',
    cursor: 'pointer',
  });

  // The journey's retention variant (2026-09-25): nothing is counted yet on Step
  // 0, so it says what is true everywhere — the whole check takes about
  // CHECK_MINUTES, the number the help line below prints through
  // TWO_MINUTE_CHECK — and offers Step 0's call-back when the offer is on screen.
  const leaveDialog = (
    <LeaveConfirmDialog
      open={guard.promptOpen}
      onStay={guard.cancelLeave}
      onLeave={guard.confirmLeave}
      journey={{ remaining: null }}
    />
  );

  if (loaderView) {
    return (
      <>
        {leaveDialog}
        <CaptureLoader view={loaderView} firstName={firstName} />
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Eligibility capture" noValidate>
      {leaveDialog}
      {/* `gps-jux-question`: the question screens' soft bloom behind the
          heading (tokens.css P12, CTO 2026-09-25) — paint only; `--intro`
          sits it higher, as this screen has no app bar above its eyebrow.
          This CTA is the terracotta conversion button, which already sits on
          its own glow (P7), so P12's Continue halo does not reach it. */}
      <JourneyScreen
        className="gps-jux-question gps-jux-question--intro"
        action={
          <JourneyButton type="submit" variant="cta" disabled={busy} arrow={!busy}>
            {busy ? 'Checking…' : 'Check my eligibility'}
          </JourneyButton>
        }
      >
        <Eyebrow>Clinician-led weight loss</Eyebrow>
        <Title>See if treatment could work for you</Title>
        {/* The same two claims the entry loader's card shows (serviceClaims.ts),
            so the loader never says something this screen no longer does. */}
        <Help>
          {clinicName} — a {TWO_MINUTE_CHECK}, reviewed by a UK clinician. {NO_CHARGE_UNTIL_APPROVED}
        </Help>

        {/* NB: no TrustStrip here. The capture-screen trust line was deliberately
            removed by the CTO (2026-07-16, pinned by ui/branding.spec) — that
            invariant wins over E3 #9, so the reassurance chips live only on the
            post-eligibility payment step. */}

        {variant === 'default' ? (
          <div
            role="group"
            aria-label="Units"
            style={{
              display: 'inline-flex',
              background: 'var(--gps-primary-light)',
              borderRadius: 'var(--gps-r-pill)',
              padding: '4px',
              gap: '2px',
              margin: '4px 0 16px',
            }}
          >
            <button type="button" style={toggleBtn(unit === 'metric')} aria-pressed={unit === 'metric'} onClick={() => switchUnit('metric')}>
              Metric
            </button>
            <button type="button" style={toggleBtn(unit === 'imperial')} aria-pressed={unit === 'imperial'} onClick={() => switchUnit('imperial')}>
              Imperial
            </button>
          </div>
        ) : null}

        {unit === 'imperial' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
            <div>
              <Field label="Height (ft)" htmlFor="cap-ft">
                <TextInput className="gps-journey-control" id="cap-ft" type="number" inputMode="numeric" placeholder="e.g. 5" value={feet} onChange={(e) => setFeet(e.target.value)} />
              </Field>
              <Field label="Height (in)" htmlFor="cap-in">
                <TextInput className="gps-journey-control" id="cap-in" type="number" inputMode="numeric" placeholder="e.g. 9" value={inches} onChange={(e) => setInches(e.target.value)} />
              </Field>
            </div>
            <div>
              <Field label="Weight (st)" htmlFor="cap-st">
                <TextInput className="gps-journey-control" id="cap-st" type="number" inputMode="numeric" placeholder="e.g. 15" value={stones} onChange={(e) => setStones(e.target.value)} />
              </Field>
              <Field label="Weight (lb)" htmlFor="cap-lb">
                <TextInput className="gps-journey-control" id="cap-lb" type="number" inputMode="numeric" placeholder="e.g. 6" value={pounds} onChange={(e) => setPounds(e.target.value)} />
              </Field>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
            <Field label="Height (cm)" htmlFor="cap-cm">
              <TextInput className="gps-journey-control" id="cap-cm" type="number" inputMode="numeric" placeholder="e.g. 175" value={cm} onChange={(e) => setCm(e.target.value)} />
            </Field>
            <Field label="Weight (kg)" htmlFor="cap-kg">
              <TextInput className="gps-journey-control" id="cap-kg" type="number" inputMode="numeric" placeholder="e.g. 98" value={kg} onChange={(e) => setKg(e.target.value)} />
            </Field>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
          <Field label="First name" htmlFor="cap-first-name">
            <TextInput className="gps-journey-control" id="cap-first-name" placeholder="e.g. Sarah" value={firstName} autoComplete="given-name" onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name" htmlFor="cap-last-name">
            <TextInput className="gps-journey-control" id="cap-last-name" placeholder="e.g. Khan" value={lastName} autoComplete="family-name" onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>

        <Field label={emailLabel} htmlFor="cap-email">
          <TextInput className="gps-journey-control" id="cap-email" type="email" placeholder="e.g. sarah@example.com" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
        </Field>

        <Field label="Phone number" htmlFor="cap-phone">
          <TextInput className="gps-journey-control" id="cap-phone" type="tel" inputMode="tel" placeholder="e.g. 07123 456789" value={phone} autoComplete="tel" onChange={(e) => setPhone(e.target.value)} />
        </Field>

        {/* The Step-0 consent tick (flag-gated — see the header). The intro and
            the withdraw line DESCRIBE the box (aria-describedby) rather than
            name it: a label is the name of one box, not a paragraph around it —
            the MarketingConsentFields grammar, sized to one channel. Static
            `cap-*` ids like every other field on this form. */}
        {consentOffered ? (
          <div style={{ marginTop: 'var(--gps-s-3)' }}>
            <p id="cap-consent-intro" style={{ margin: '0 0 4px', fontSize: '14.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
              {STEP0_CONSENT_WORDING.intro}
            </p>
            <CheckboxRow
              label={STEP0_CONSENT_WORDING.email}
              checked={marketingEmail}
              describedBy="cap-consent-intro cap-consent-withdraw"
              onChange={() => setMarketingEmail((ticked) => !ticked)}
            />
            <p id="cap-consent-withdraw" style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
              {STEP0_CONSENT_WORDING.withdraw}
            </p>
          </div>
        ) : null}

        {error ? (
          <p ref={errorRef} role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px', margin: '4px 0 0' }}>
            {error}
          </p>
        ) : null}
        <p style={{ fontSize: '12px', color: 'var(--gps-muted)', marginTop: 'var(--gps-s-3)' }}>
          We use your details to check whether treatment could be suitable and to save your progress.
          A UK clinician reviews everything before any treatment is offered.
        </p>
      </JourneyScreen>
    </form>
  );
}
